import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getRentabilidadVentas,
} from "../helpers/rutaVentas";

import logo from "../images/ridersmiami-logo.png";

/* =========================================================
   FECHAS
========================================================= */

const todayLocalISO = () => {
  const d = new Date();

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

const firstDayMonthISO = () => {
  const d = new Date();

  d.setDate(1);

  d.setMinutes(
    d.getMinutes() -
      d.getTimezoneOffset()
  );

  return d
    .toISOString()
    .slice(0, 10);
};

/* =========================================================
   HELPERS
========================================================= */

const clean = (v) =>
  String(v ?? "").trim();

const formatDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const m = value.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);

  if (
    Number.isNaN(
      d.getTime()
    )
  ) {
    return "";
  }

  return d.toLocaleDateString(
    "es-AR"
  );
};

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$ ${n.toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}`;
};

const formatNumero = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  );
};

const formatPorcentaje = (
  value
) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "-";
  }

  return `${Number(
    value || 0
  ).toLocaleString(
    "es-AR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )}%`;
};

const nombreUsuario = (
  usuario
) => {
  if (!usuario) return "";

  if (
    typeof usuario === "string"
  ) {
    return "";
  }

  return (
    [
      usuario.nombres,
      usuario.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    [
      usuario.nombre,
      usuario.apellido,
    ]
      .filter(Boolean)
      .join(" ") ||
    usuario.email ||
    ""
  );
};

const nombreSede = (
  sede
) => {
  const s = clean(
    sede
  ).toUpperCase();

  if (s === "YERBA_BUENA") {
    return "YERBA BUENA";
  }

  if (s === "SIN_SEDE") {
    return "SIN SEDE";
  }

  return s;
};

const cargarImagen = (
  src
) =>
  new Promise(
    (
      resolve,
      reject
    ) => {
      const img =
        new Image();

      img.crossOrigin =
        "Anonymous";

      img.onload = () =>
        resolve(img);

      img.onerror =
        reject;

      img.src = src;
    }
  );

/* =========================================================
   COMPONENTE
========================================================= */

export default function InformeRentabilidadVentas() {
  const [
    desde,
    setDesde,
  ] = useState(
    firstDayMonthISO()
  );

  const [
    hasta,
    setHasta,
  ] = useState(
    todayLocalISO()
  );

  const [
    sede,
    setSede,
  ] = useState("TODAS");

  const [
    buscar,
    setBuscar,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    filas,
    setFilas,
  ] = useState([]);

  const [
    resumenSedes,
    setResumenSedes,
  ] = useState([]);

  const [
    totalesBackend,
    setTotalesBackend,
  ] = useState({
    cantidadVentas: 0,
    cantidadItems: 0,
    cantidadProductos: 0,
    totalVentaRealUSD: 0,
    totalVentaCalculableUSD: 0,
    totalCostoUSD: 0,
    totalRentabilidadUSD: 0,
    margenPorcentaje: 0,
    sinCostoHistorico: 0,
  });

  /* =========================================================
     CARGAR REPORTE
  ========================================================= */

  const cargarReporte =
    async () => {
      setLoading(true);
      setError("");

      try {
        const resp =
          await getRentabilidadVentas(
            {
              desde,
              hasta,
              sede,
            }
          );

        setFilas(
          Array.isArray(
            resp?.filas
          )
            ? resp.filas
            : []
        );

        setResumenSedes(
          Array.isArray(
            resp?.resumenSedes
          )
            ? resp.resumenSedes
            : []
        );

        setTotalesBackend({
          cantidadVentas:
            Number(
              resp?.totales
                ?.cantidadVentas ||
                0
            ),

          cantidadItems:
            Number(
              resp?.totales
                ?.cantidadItems ||
                0
            ),

          cantidadProductos:
            Number(
              resp?.totales
                ?.cantidadProductos ||
                0
            ),

          totalVentaRealUSD:
            Number(
              resp?.totales
                ?.totalVentaRealUSD ||
                0
            ),

          totalVentaCalculableUSD:
            Number(
              resp?.totales
                ?.totalVentaCalculableUSD ||
                0
            ),

          totalCostoUSD:
            Number(
              resp?.totales
                ?.totalCostoUSD ||
                0
            ),

          totalRentabilidadUSD:
            Number(
              resp?.totales
                ?.totalRentabilidadUSD ||
                0
            ),

          margenPorcentaje:
            Number(
              resp?.totales
                ?.margenPorcentaje ||
                0
            ),

          sinCostoHistorico:
            Number(
              resp?.totales
                ?.sinCostoHistorico ||
                0
            ),
        });
      } catch (err) {
        console.error(
          "Error informe rentabilidad:",
          err
        );

        setError(
          err?.response?.data
            ?.error ||
            err?.response?.data
              ?.message ||
            err?.message ||
            "Error al cargar el informe de rentabilidad."
        );

        setFilas([]);
        setResumenSedes([]);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    cargarReporte();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     BUSCADOR LOCAL
  ========================================================= */

  const filasFiltradas =
    useMemo(() => {
      const texto =
        clean(
          buscar
        ).toUpperCase();

      if (!texto) {
        return filas;
      }

      return filas.filter(
        (fila) => {
          const cadena = [
            fila.nroventa,
            fila.sede,
            fila.codprod,
            fila.descripcionProducto,
            fila.modeloProducto,
            fila.marcaNombre,
            nombreUsuario(
              fila.vendedor
            ),
          ]
            .join(" ")
            .toUpperCase();

          return cadena.includes(
            texto
          );
        }
      );
    }, [
      filas,
      buscar,
    ]);

  /*
    Cuando hay búsqueda local,
    recalculamos los totales visibles
    para que las tarjetas coincidan
    con la tabla mostrada.
  */
  const totales =
    useMemo(() => {
      if (
        !clean(buscar)
      ) {
        return totalesBackend;
      }

      const ventasUnicas =
        new Set(
          filasFiltradas.map(
            (fila) =>
              String(
                fila.ventaId
              )
          )
        );

      let cantidadProductos =
        0;

      let totalVentaRealUSD =
        0;

      let totalVentaCalculableUSD =
        0;

      let totalCostoUSD =
        0;

      let totalRentabilidadUSD =
        0;

      let sinCostoHistorico =
        0;

      filasFiltradas.forEach(
        (fila) => {
          const cantidad =
            Number(
              fila.cantidad ||
                0
            );

          cantidadProductos +=
            cantidad;

          totalVentaRealUSD +=
            Number(
              fila.ventaRealUSD ||
                0
            );

          if (
            fila.tieneCostoHistorico
          ) {
            totalVentaCalculableUSD +=
              Number(
                fila.ventaRealUSD ||
                  0
              );

            totalCostoUSD +=
              Number(
                fila.costoTotalUSD ||
                  0
              );

            totalRentabilidadUSD +=
              Number(
                fila.rentabilidadUSD ||
                  0
              );
          } else {
            sinCostoHistorico +=
              cantidad;
          }
        }
      );

      const margenPorcentaje =
        totalVentaCalculableUSD >
        0
          ? (
              totalRentabilidadUSD /
              totalVentaCalculableUSD
            ) *
            100
          : 0;

      return {
        cantidadVentas:
          ventasUnicas.size,

        cantidadItems:
          filasFiltradas.length,

        cantidadProductos,

        totalVentaRealUSD,

        totalVentaCalculableUSD,

        totalCostoUSD,

        totalRentabilidadUSD,

        margenPorcentaje,

        sinCostoHistorico,
      };
    }, [
      buscar,
      filasFiltradas,
      totalesBackend,
    ]);

  /* =========================================================
     LIMPIAR
  ========================================================= */

  const limpiarFiltros =
    () => {
      setDesde(
        firstDayMonthISO()
      );

      setHasta(
        todayLocalISO()
      );

      setSede("TODAS");
      setBuscar("");
    };

  /* =========================================================
     PDF
  ========================================================= */

  const generarPDF =
    async () => {
      try {
        const doc =
          new jsPDF(
            "landscape",
            "mm",
            "a4"
          );

        try {
          const logoImg =
            await cargarImagen(
              logo
            );

          doc.addImage(
            logoImg,
            "PNG",
            10,
            8,
            34,
            12
          );
        } catch (e) {
          console.warn(
            "No se pudo cargar logo PDF",
            e
          );
        }

        doc.setFont(
          "helvetica",
          "bold"
        );

        doc.setFontSize(
          16
        );

        doc.text(
          "Informe de Rentabilidad de Ventas",
          50,
          14
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(
          10
        );

        doc.text(
          "Riders Miami",
          50,
          20
        );

        doc.text(
          `Desde: ${
            desde || "-"
          }   Hasta: ${
            hasta || "-"
          }`,
          287,
          14,
          {
            align: "right",
          }
        );

        doc.text(
          `Sede: ${nombreSede(
            sede
          )}`,
          287,
          20,
          {
            align: "right",
          }
        );

        doc.setDrawColor(
          180
        );

        doc.line(
          10,
          24,
          287,
          24
        );

        /*
          RESUMEN
        */

        const resumen = [
          {
            label:
              "Ventas",
            value:
              String(
                totales
                  .cantidadVentas ||
                  0
              ),
          },

          {
            label:
              "Productos",
            value:
              formatNumero(
                totales
                  .cantidadProductos ||
                  0
              ),
          },

          {
            label:
              "Venta real",
            value:
              formatUSDPdf(
                totales
                  .totalVentaRealUSD ||
                  0
              ),
          },

          {
            label:
              "Venta calculable",
            value:
              formatUSDPdf(
                totales
                  .totalVentaCalculableUSD ||
                  0
              ),
          },

          {
            label:
              "Costo",
            value:
              formatUSDPdf(
                totales
                  .totalCostoUSD ||
                  0
              ),
          },

          {
            label:
              "Rentabilidad",
            value:
              formatUSDPdf(
                totales
                  .totalRentabilidadUSD ||
                  0
              ),
          },

          {
            label:
              "Margen",
            value:
              formatPorcentaje(
                totales
                  .margenPorcentaje
              ),
          },

          {
            label:
              "Sin costo histórico",
            value:
              formatNumero(
                totales
                  .sinCostoHistorico ||
                  0
              ),
          },
        ];

        const boxW = 32;
        const boxH = 12;
        const gap = 2;

        resumen.forEach(
          (
            item,
            index
          ) => {
            const x =
              9 +
              index *
                (boxW + gap);

            const y = 28;

            doc.setDrawColor(
              210
            );

            doc.roundedRect(
              x,
              y,
              boxW,
              boxH,
              1.5,
              1.5
            );

            doc.setFont(
              "helvetica",
              "normal"
            );

            doc.setFontSize(
              6.4
            );

            doc.text(
              item.label,
              x + 1.5,
              y + 4
            );

            doc.setFont(
              "helvetica",
              "bold"
            );

            doc.setFontSize(
              6.8
            );

            doc.text(
              String(
                item.value
              ),
              x + 1.5,
              y + 9
            );
          }
        );

        /*
          TABLA
        */

        const body =
          filasFiltradas.map(
            (fila) => [
              formatDate(
                fila.fecha
              ),

              fila.nroventa ||
                "",

              nombreSede(
                fila.sede
              ),

              nombreUsuario(
                fila.vendedor
              ),

              fila.codprod ||
                "",

              fila.descripcionProducto ||
                fila.modeloProducto ||
                "",

              formatNumero(
                fila.cantidad
              ),

              formatUSDPdf(
                fila.precioVentaUSD
              ),

              fila.tieneCostoHistorico
                ? formatUSDPdf(
                    fila.costoUnitarioUSD
                  )
                : "SIN HIST.",

              formatUSDPdf(
                fila.ventaRealUSD
              ),

              fila.tieneCostoHistorico
                ? formatUSDPdf(
                    fila.costoTotalUSD
                  )
                : "-",

              fila.tieneCostoHistorico
                ? formatUSDPdf(
                    fila.rentabilidadUSD
                  )
                : "-",

              fila.tieneCostoHistorico
                ? formatPorcentaje(
                    fila.margenPorcentaje
                  )
                : "-",
            ]
          );

        autoTable(
          doc,
          {
            startY: 45,

            theme:
              "grid",

            margin: {
              left: 6,
              right: 6,
              bottom: 10,
            },

            head: [
              [
                "Fecha",
                "Venta",
                "Sede",
                "Vendedor",
                "Cod.",
                "Producto",
                "Cant.",
                "Precio real",
                "Costo unit.",
                "Venta real",
                "Costo total",
                "Rentabilidad",
                "Margen",
              ],
            ],

            body,

            foot: [
              [
                "TOTALES",
                "",
                "",
                "",
                "",
                "",
                formatNumero(
                  totales
                    .cantidadProductos
                ),
                "",
                "",
                formatUSDPdf(
                  totales
                    .totalVentaRealUSD
                ),
                formatUSDPdf(
                  totales
                    .totalCostoUSD
                ),
                formatUSDPdf(
                  totales
                    .totalRentabilidadUSD
                ),
                formatPorcentaje(
                  totales
                    .margenPorcentaje
                ),
              ],
            ],

            showHead:
              "everyPage",

            showFoot:
              "lastPage",

            styles: {
              font:
                "helvetica",

              fontSize:
                6,

              cellPadding:
                0.8,

              valign:
                "middle",

              lineColor: [
                210,
                210,
                210,
              ],

              lineWidth:
                0.15,
            },

            headStyles: {
              fillColor: [
                33,
                37,
                41,
              ],

              textColor: [
                255,
                255,
                255,
              ],

              fontStyle:
                "bold",

              halign:
                "center",
            },

            footStyles: {
              fillColor: [
                238,
                242,
                247,
              ],

              textColor: [
                20,
                20,
                20,
              ],

              fontStyle:
                "bold",
            },

            columnStyles: {
              0: {
                cellWidth: 16,
                halign:
                  "center",
              },

              1: {
                cellWidth: 10,
                halign:
                  "center",
              },

              2: {
                cellWidth: 20,
              },

              3: {
                cellWidth: 31,
              },

              4: {
                cellWidth: 12,
                halign:
                  "center",
              },

              5: {
                cellWidth: 48,
              },

              6: {
                cellWidth: 10,
                halign:
                  "right",
              },

              7: {
                cellWidth: 22,
                halign:
                  "right",
              },

              8: {
                cellWidth: 22,
                halign:
                  "right",
              },

              9: {
                cellWidth: 22,
                halign:
                  "right",
              },

              10: {
                cellWidth: 22,
                halign:
                  "right",
              },

              11: {
                cellWidth: 24,
                halign:
                  "right",
              },

              12: {
                cellWidth: 15,
                halign:
                  "right",
              },
            },

            didDrawPage:
              (data) => {
                const pageHeight =
                  doc.internal
                    .pageSize
                    .getHeight();

                doc.setFontSize(
                  7
                );

                doc.setTextColor(
                  110
                );

                doc.text(
                  `Página ${data.pageNumber}`,
                  287,
                  pageHeight -
                    4,
                  {
                    align:
                      "right",
                  }
                );
              },
          }
        );

        doc.save(
          `rentabilidad-${sede.toLowerCase()}-${desde}-${hasta}.pdf`
        );
      } catch (err) {
        console.error(
          "Error PDF rentabilidad:",
          err
        );

        alert(
          "No se pudo generar el PDF."
        );
      }
    };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>

          {/* ================= CABECERA ================= */}

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h4 className="mb-0">
                Informe de Rentabilidad de Ventas
              </h4>

              <small className="text-muted">
                Precio real vendido vs. costo histórico
              </small>
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={
                  limpiarFiltros
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={
                  cargarReporte
                }
                disabled={
                  loading
                }
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={
                  generarPDF
                }
                disabled={
                  loading ||
                  filasFiltradas.length ===
                    0
                }
              >
                PDF
              </Button>
            </div>
          </div>

          {/* ================= FILTROS ================= */}

          <Row className="g-2 mb-3 align-items-end">
            <Col
              xs={12}
              sm={6}
              md={3}
            >
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                value={
                  desde
                }
                onChange={(
                  e
                ) =>
                  setDesde(
                    e.target
                      .value
                  )
                }
                size="sm"
              />
            </Col>

            <Col
              xs={12}
              sm={6}
              md={3}
            >
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                value={
                  hasta
                }
                onChange={(
                  e
                ) =>
                  setHasta(
                    e.target
                      .value
                  )
                }
                size="sm"
              />
            </Col>

            <Col
              xs={12}
              sm={6}
              md={3}
            >
              <Form.Label>
                Sede
              </Form.Label>

              <Form.Select
                value={
                  sede
                }
                onChange={(
                  e
                ) =>
                  setSede(
                    e.target
                      .value
                  )
                }
                size="sm"
              >
                <option value="TODAS">
                  TODAS
                </option>

                <option value="MIAMI">
                  MIAMI
                </option>

                <option value="RESTO">
                  RESTO
                </option>

                <option value="TUCUMAN">
                  TUCUMAN
                </option>

                <option value="YERBA_BUENA">
                  YERBA BUENA
                </option>
              </Form.Select>
            </Col>

            <Col
              xs={12}
              sm={6}
              md={3}
            >
              <Form.Label>
                Buscar
              </Form.Label>

              <Form.Control
                value={
                  buscar
                }
                onChange={(
                  e
                ) =>
                  setBuscar(
                    e.target
                      .value
                  )
                }
                placeholder="Producto, vendedor, venta..."
                size="sm"
              />
            </Col>
          </Row>

          {/* ================= ERROR ================= */}

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* ================= TOTALES ================= */}

          <Row className="g-2 mb-3">

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Ventas
                  </small>

                  <h5 className="mb-0">
                    {
                      totales.cantidadVentas
                    }
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Productos vendidos
                  </small>

                  <h5 className="mb-0">
                    {formatNumero(
                      totales.cantidadProductos
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Venta real
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      totales.totalVentaRealUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Costo histórico
                  </small>

                  <h5 className="mb-0 text-danger">
                    {formatUSD(
                      totales.totalCostoUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Rentabilidad
                  </small>

                  <h5
                    className={`mb-0 ${
                      Number(
                        totales.totalRentabilidadUSD
                      ) >= 0
                        ? "text-success"
                        : "text-danger"
                    }`}
                  >
                    {formatUSD(
                      totales.totalRentabilidadUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Margen
                  </small>

                  <h5 className="mb-0">
                    {formatPorcentaje(
                      totales.margenPorcentaje
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3} xl>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Sin costo histórico
                  </small>

                  <h5
                    className={`mb-0 ${
                      Number(
                        totales.sinCostoHistorico
                      ) > 0
                        ? "text-warning"
                        : ""
                    }`}
                  >
                    {formatNumero(
                      totales.sinCostoHistorico
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ================= AVISO HISTÓRICO ================= */}

          {Number(
            totales.sinCostoHistorico
          ) > 0 && (
            <div className="alert alert-warning py-2">
              Hay{" "}
              <strong>
                {formatNumero(
                  totales.sinCostoHistorico
                )}
              </strong>{" "}
              producto(s) vendidos sin costo histórico.
              Esos registros se muestran en la venta real,
              pero no se utilizan para calcular costo,
              rentabilidad ni margen.
            </div>
          )}

          {/* ================= RESUMEN POR SEDE ================= */}

          {resumenSedes.length >
            0 && (
            <>
              <h6 className="mt-3 mb-2">
                Resumen por sede
              </h6>

              <div className="table-responsive mb-3">
                <Table
                  bordered
                  hover
                  size="sm"
                  className="align-middle mb-0"
                >
                  <thead className="table-light">
                    <tr>
                      <th>
                        Sede
                      </th>

                      <th className="text-end">
                        Productos
                      </th>

                      <th className="text-end">
                        Venta real
                      </th>

                      <th className="text-end">
                        Venta calculable
                      </th>

                      <th className="text-end">
                        Costo
                      </th>

                      <th className="text-end">
                        Rentabilidad
                      </th>

                      <th className="text-end">
                        Margen
                      </th>

                      <th className="text-end">
                        Sin histórico
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {resumenSedes.map(
                      (
                        item
                      ) => (
                        <tr
                          key={
                            item.sede
                          }
                        >
                          <td className="fw-bold">
                            {nombreSede(
                              item.sede
                            )}
                          </td>

                          <td className="text-end">
                            {formatNumero(
                              item.cantidadProductos
                            )}
                          </td>

                          <td className="text-end">
                            {formatUSD(
                              item.ventaRealUSD
                            )}
                          </td>

                          <td className="text-end">
                            {formatUSD(
                              item.ventaCalculableUSD
                            )}
                          </td>

                          <td className="text-end text-danger">
                            {formatUSD(
                              item.costoUSD
                            )}
                          </td>

                          <td
                            className={`text-end fw-bold ${
                              Number(
                                item.rentabilidadUSD
                              ) >= 0
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            {formatUSD(
                              item.rentabilidadUSD
                            )}
                          </td>

                          <td className="text-end fw-bold">
                            {formatPorcentaje(
                              item.margenPorcentaje
                            )}
                          </td>

                          <td className="text-end">
                            {formatNumero(
                              item.sinCostoHistorico
                            )}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {/* ================= DETALLE ================= */}

          <h6 className="mt-3 mb-2">
            Detalle de rentabilidad
          </h6>

          <div
            className="table-responsive"
            style={{
              maxHeight:
                "62vh",
            }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
              style={{
                minWidth:
                  "1500px",
              }}
            >
              <thead
                className="table-dark"
                style={{
                  position:
                    "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th>
                    Fecha
                  </th>

                  <th>
                    Venta
                  </th>

                  <th>
                    Sede
                  </th>

                  <th>
                    Vendedor
                  </th>

                  <th>
                    Código
                  </th>

                  <th>
                    Producto
                  </th>

                  <th className="text-end">
                    Cant.
                  </th>

                  <th className="text-end">
                    Precio real unit.
                  </th>

                  <th className="text-end">
                    Costo unit.
                  </th>

                  <th className="text-end">
                    Venta real
                  </th>

                  <th className="text-end">
                    Costo total
                  </th>

                  <th className="text-end">
                    Rentabilidad
                  </th>

                  <th className="text-end">
                    Margen
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="13"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                      />{" "}
                      Cargando informe...
                    </td>
                  </tr>
                ) : filasFiltradas.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="13"
                      className="text-center py-4 text-muted"
                    >
                      No hay datos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  filasFiltradas.map(
                    (
                      fila,
                      index
                    ) => (
                      <tr
                        key={`${fila.ventaId}-${fila.itemId}-${index}`}
                      >
                        <td>
                          {formatDate(
                            fila.fecha
                          )}
                        </td>

                        <td>
                          {
                            fila.nroventa
                          }
                        </td>

                        <td className="fw-bold">
                          {nombreSede(
                            fila.sede
                          )}
                        </td>

                        <td>
                          {nombreUsuario(
                            fila.vendedor
                          )}
                        </td>

                        <td>
                          {
                            fila.codprod
                          }
                        </td>

                        <td>
                          {fila.descripcionProducto ||
                            fila.modeloProducto}
                        </td>

                        <td className="text-end">
                          {formatNumero(
                            fila.cantidad
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            fila.precioVentaUSD
                          )}
                        </td>

                        <td className="text-end text-danger fw-bold">
                          {fila.tieneCostoHistorico
                            ? formatUSD(
                                fila.costoUnitarioUSD
                              )
                            : (
                              <span className="badge bg-warning text-dark">
                                SIN HISTÓRICO
                              </span>
                            )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            fila.ventaRealUSD
                          )}
                        </td>

                        <td className="text-end text-danger fw-bold">
                          {fila.tieneCostoHistorico
                            ? formatUSD(
                                fila.costoTotalUSD
                              )
                            : "-"}
                        </td>

                        <td
                          className={`text-end fw-bold ${
                            fila.tieneCostoHistorico
                              ? Number(
                                  fila.rentabilidadUSD
                                ) >= 0
                                ? "text-success"
                                : "text-danger"
                              : ""
                          }`}
                        >
                          {fila.tieneCostoHistorico
                            ? formatUSD(
                                fila.rentabilidadUSD
                              )
                            : "-"}
                        </td>

                        <td className="text-end fw-bold">
                          {fila.tieneCostoHistorico
                            ? formatPorcentaje(
                                fila.margenPorcentaje
                              )
                            : "-"}
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>

              {!loading &&
                filasFiltradas.length >
                  0 && (
                  <tfoot>
                    <tr className="table-light fw-bold">
                      <th colSpan="6">
                        Totales
                      </th>

                      <th className="text-end">
                        {formatNumero(
                          totales.cantidadProductos
                        )}
                      </th>

                      <th></th>

                      <th></th>

                      <th className="text-end">
                        {formatUSD(
                          totales.totalVentaRealUSD
                        )}
                      </th>

                      <th className="text-end text-danger">
                        {formatUSD(
                          totales.totalCostoUSD
                        )}
                      </th>

                      <th
                        className={`text-end ${
                          Number(
                            totales.totalRentabilidadUSD
                          ) >= 0
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        {formatUSD(
                          totales.totalRentabilidadUSD
                        )}
                      </th>

                      <th className="text-end">
                        {formatPorcentaje(
                          totales.margenPorcentaje
                        )}
                      </th>
                    </tr>
                  </tfoot>
                )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}